/**
 * Partner Network Progress Component
 *
 * Displays progress towards building a complete partner network across all trade categories.
 * Shows filled vs unfilled categories, progress percentage, and visual indicator.
 * Used on the Dashboard to motivate users to connect with partners in all trades.
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Users, CheckCircle, CheckCircle2, ArrowRight, Sparkles, Settings2 } from 'lucide-react';
import EditPartnerNetworkModal from '@/components/EditPartnerNetworkModal';

interface PartnerNetworkCategory {
  id: string;
  name: string;
  customName: string | null;
  isActive: boolean;
  isFilled: boolean;
  partnerCount: number;
}

interface PartnerNetworkProgressData {
  userProfileId: string;
  totalCategories: number;
  filledCategories: number;
  progressPercentage: number;
  categories: PartnerNetworkCategory[];
}

interface PartnerNetworkProgressProps {
  userId?: string;
  compact?: boolean;
  showCategoryDetails?: boolean;
  partnerConnections?: number;
  onNavigate?: () => void;
}

/** Returns a Tailwind class that colors the progress bar based on completion % */
function getProgressBarColor(pct: number): string {
  if (pct >= 80) return '[&>div]:bg-green-600';
  if (pct >= 60) return '[&>div]:bg-green-400';
  if (pct >= 40) return '[&>div]:bg-yellow-400';
  if (pct >= 20) return '[&>div]:bg-orange-500';
  return '[&>div]:bg-red-500';
}

export function PartnerNetworkProgress({
  userId,
  compact = false,
  showCategoryDetails = false,
  partnerConnections,
  onNavigate,
}: PartnerNetworkProgressProps) {
  const [progressData, setProgressData] = useState<PartnerNetworkProgressData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();

  /**
   * Jump from a trade category card to the partners the user already has in that trade.
   * `onNavigate` closes the containing modal first (the Network Health modal passes it),
   * then the `category` param tells the Network page which group to scroll to.
   */
  const handleCategoryClick = (category: PartnerNetworkCategory) => {
    onNavigate?.();
    navigate(`/network?category=${encodeURIComponent(category.name)}`);
  };

  useEffect(() => {
    fetchProgress();
  }, [userId]);

  const fetchProgress = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.get<PartnerNetworkProgressData>('/api/workflows/partner-network-progress');

      if (response) {
        // Validate response structure
        if (!response.categories || !Array.isArray(response.categories)) {
          console.error('Invalid API response structure:', response);
          setError('Unable to load progress data - invalid response format');
          return;
        }
        setProgressData(response);
      }
    } catch (err: any) {
      console.error('Error fetching partner network progress:', err);

      // Provide more specific error messages
      if (err.response?.status === 401) {
        setError('Please log in to view your partner network progress');
      } else if (err.response?.status === 404) {
        setError('Partner network progress endpoint not found');
      } else {
        setError('Unable to load progress data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (error) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <p className="text-sm text-amber-700">{error}</p>
          <Button
            onClick={fetchProgress}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!progressData) {
    return null;
  }

  const {
    totalCategories,
    filledCategories,
    progressPercentage,
    categories
  } = progressData;

  // Safety check: ensure categories is an array
  if (!Array.isArray(categories)) {
    console.error('PartnerNetworkProgress: categories is not an array', { progressData });
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <p className="text-sm text-amber-700">Unable to load progress data - invalid response format</p>
          <Button
            onClick={fetchProgress}
            variant="outline"
            size="sm"
            className="mt-2"
          >
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const isComplete = progressPercentage >= 100;

  // Compact view for smaller dashboard widgets
  if (compact) {
    return (
      <Card className={`shadow-md hover:shadow-lg transition-all duration-300 ${
        isComplete
          ? 'border-4 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 ring-4 ring-green-300/60'
          : 'border border-gray-200'
      }`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isComplete ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                <Users className={`w-5 h-5 ${isComplete ? 'text-green-600' : 'text-blue-600'}`} />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Partner Network</h3>
                <p className="text-xs text-gray-500">
                  {filledCategories} of {totalCategories} trades covered
                </p>
              </div>
            </div>
            {isComplete && (
              <Badge className="bg-green-100 text-green-700 border-0">
                <Sparkles className="w-3 h-3 mr-1" />
                Complete!
              </Badge>
            )}
          </div>

          <Progress
            value={progressPercentage}
            className={`h-3 ${getProgressBarColor(progressPercentage)}`}
          />

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm font-medium text-gray-700">{Math.round(progressPercentage)}%</span>
            <Link to="/network">
              <Button size="sm" variant="ghost" className="h-auto px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                View Details
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Full view with category details
  return (
  <>
    <Card className={`shadow-md hover:shadow-lg transition-all duration-300 ${
      isComplete
        ? 'border-4 border-green-400 bg-gradient-to-r from-green-50 to-emerald-50 ring-4 ring-green-300/60'
        : 'border border-gray-200'
    }`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl font-semibold text-gray-800 tracking-tight leading-snug flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Partner Network Health
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
              className="text-gray-500 hover:text-blue-600"
              title="Edit partner trades"
            >
              <Settings2 className="w-4 h-4" />
              <span className="ml-1">Edit</span>
            </Button>
            {isComplete && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-green-200 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Complete
              </Badge>
            )}
          </div>
        </div>

        {/* Two-stat split row */}
        <div className="flex gap-3 mt-3">
          <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-800 leading-none">
              {filledCategories}
              <span className="text-base font-medium text-gray-500">/{totalCategories}</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">trades covered</p>
          </div>
          <div className="flex-1 bg-gray-50 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-800 leading-none">
              {partnerConnections ?? '—'}
            </p>
            <p className="text-xs text-gray-500 mt-1">partner connections</p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Progress Overview */}
        <div className="space-y-2">
          <div className="flex items-end justify-between">
            <span className="text-sm text-gray-500">Network coverage</span>
            <span className="text-sm font-semibold text-gray-700">{Math.round(progressPercentage)}%</span>
          </div>
          {/* Progress bar with multi-colored segments preview */}
          <div className="relative">
            {/* Background: Semi-transparent preview of all color segments */}
            <div className="h-4 w-full rounded-full overflow-hidden relative">
              {/* Color segments background (semi-transparent) */}
              <div className="absolute inset-0 flex">
                {/* 0-20%: Red */}
                <div className="h-full bg-red-500/30" style={{ width: '20%' }} />
                {/* 20-40%: Orange */}
                <div className="h-full bg-orange-500/30" style={{ width: '20%' }} />
                {/* 40-60%: Yellow */}
                <div className="h-full bg-yellow-400/30" style={{ width: '20%' }} />
                {/* 60-80%: Light Green */}
                <div className="h-full bg-green-400/30" style={{ width: '20%' }} />
                {/* 80-100%: Dark Green */}
                <div className="h-full bg-green-600/30" style={{ width: '20%' }} />
              </div>

              {/* Foreground: Actual progress (solid color) */}
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-500 ease-out ${
                  progressPercentage >= 80 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                  progressPercentage >= 60 ? 'bg-gradient-to-r from-green-400 to-green-500' :
                  progressPercentage >= 40 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' :
                  progressPercentage >= 20 ? 'bg-gradient-to-r from-orange-400 to-orange-500' :
                  'bg-gradient-to-r from-red-500 to-red-600'
                }`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            {/* Round dot at current progress position */}
            {progressPercentage > 0 && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white shadow-md transition-all duration-500 ease-out"
                style={{
                  left: `${progressPercentage}%`,
                  transform: 'translate(-50%, -50%)',
                  backgroundColor: progressPercentage >= 80 ? '#16a34a' :
                                 progressPercentage >= 60 ? '#22c55e' :
                                 progressPercentage >= 40 ? '#facc15' :
                                 progressPercentage >= 20 ? '#fb923c' : '#ef4444'
                }}
              />
            )}
          </div>
        </div>

        {/* Sub headers */}
        <div className="space-y-1 pt-2">
          <h4 className="text-sm font-semibold text-gray-800">
            Add partners in these trades to unlock more referral opportunities
          </h4>
          <p className="text-xs text-gray-500">
            You can add unlimited partners.
          </p>
        </div>

        {/* Category Details */}
        {showCategoryDetails && categories.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Trade Categories</h4>
            <div className="grid grid-cols-3 gap-2">
              {categories
                .filter(cat => cat.isActive)
                .map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryClick(category)}
                    aria-label={`View my ${category.customName || category.name} partners`}
                    className={`relative p-2 rounded-lg border min-h-[3.5rem] flex flex-col justify-center text-left w-full transition-colors hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 ${
                      category.isFilled
                        ? 'bg-green-50 border-green-200 hover:bg-green-100'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {category.isFilled && (
                      <CheckCircle className="absolute -top-1.5 -left-1.5 w-4 h-4 text-green-600 bg-white rounded-full" />
                    )}
                    <p className={`text-xs font-medium break-words ${
                      category.isFilled ? 'text-green-900' : 'text-gray-700'
                    }`}>
                      {category.customName || category.name}
                    </p>
                    {category.partnerCount > 0 && (
                      <p className="text-xs text-gray-500">
                        {category.partnerCount} partner{category.partnerCount !== 1 ? 's' : ''}
                      </p>
                    )}
                  </button>
                ))}
            </div>
          </div>
        )}

        {/* Call to Action */}
        {!isComplete && (
          <Link to="/network?tab=suggestions" className="block" onClick={onNavigate}>
            <Button className="w-full bg-blue-500 hover:bg-blue-600 text-white">
              <Users className="w-4 h-4 mr-2" />
              Add More Partners
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>

    {/* Edit Partner Network Modal */}
    {progressData?.userProfileId && (
      <EditPartnerNetworkModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userProfileId={progressData.userProfileId}
        onSuccess={fetchProgress}
      />
    )}
  </>
  );
}
